<template>
    <Transition name="template-slide">
        <div class="template_dropdown" v-if="show" v-click-outside="onClickOutside">
            <div class="template_search_wrapper" v-if="!loading && !error">
                <div class="template_search_field">
                    <img class="template_search_icon" src="/assets/img/search.svg" />
                    <input class="template_search_input" :placeholder="$t('chat.templates_search')" v-model="searchQuery" ref="searchInput" @keydown.esc="$emit('close')" @keydown.down.prevent="moveHighlight(1)" @keydown.up.prevent="moveHighlight(-1)" @keydown.enter.prevent="selectHighlighted" />
                </div>
            </div>
            <div class="template_list">
                <div v-if="loading" class="template_empty">
                    <span>{{ $t('chat.templates_loading') }}</span>
                </div>
                <div v-else-if="error" class="template_empty">
                    <img class="template_empty_icon" src="/assets/img/search.svg" />
                    <span>{{ $t('chat.templates_error') }}</span>
                </div>
                <div v-else-if="filtered.length === 0" class="template_empty">
                    <img class="template_empty_icon" src="/assets/img/search.svg" />
                    <span>{{ $t('chat.templates_empty') }}</span>
                </div>
                <template v-else>
                    <div v-for="(template, index) in filtered" :key="template.id" class="template_item" :class="{ template_item_highlighted: index === highlightedIndex }" @click="$emit('select', template)" @mouseenter="highlightedIndex = index">
                        <div class="template_text">{{ template.text }}</div>
                    </div>
                </template>
            </div>
        </div>
    </Transition>
</template>

<script lang="ts">
    import { defineComponent, PropType, nextTick } from 'vue';
    import { ChatTemplate } from '../../models/ChatTemplate';

    export default defineComponent({
        name: 'ChatTemplateDropdown',
        props: {
            show: {
                type: Boolean,
                required: true
            },
            templates: {
                type: Array as PropType<ChatTemplate[]>,
                required: true
            },
            loading: {
                type: Boolean,
                default: false
            },
            error: {
                type: Boolean,
                default: false
            }
        },
        emits: ['select', 'close'],
        data() {
            return {
                searchQuery: '',
                highlightedIndex: -1
            }
        },
        computed: {
            filtered(): ChatTemplate[] {
                if (!this.searchQuery) return this.templates;
                const q = this.searchQuery.toLowerCase();
                return this.templates.filter(t => t.text.toLowerCase().includes(q));
            }
        },
        watch: {
            show(val: boolean) {
                if (val) {
                    this.searchQuery = '';
                    this.highlightedIndex = -1;
                    nextTick(() => {
                        (this.$refs.searchInput as HTMLInputElement)?.focus();
                    });
                }
            },
            searchQuery() {
                this.highlightedIndex = -1;
            }
        },
        methods: {
            onClickOutside() {
                this.$emit('close');
            },
            moveHighlight(direction: number) {
                const count = this.filtered.length;
                if (count === 0) return;

                if (this.highlightedIndex === -1) {
                    this.highlightedIndex = direction === 1 ? 0 : count - 1;
                } else {
                    this.highlightedIndex = (this.highlightedIndex + direction + count) % count;
                }

                nextTick(() => {
                    const el = document.querySelector('.template_item_highlighted');
                    if (el) el.scrollIntoView({ block: 'nearest' });
                });
            },
            selectHighlighted() {
                if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filtered.length) {
                    this.$emit('select', this.filtered[this.highlightedIndex]);
                }
            }
        }
    });
</script>

<style scoped>
    .template_dropdown {
        position: absolute;
        bottom: 100%;
        left: 12px;
        right: 12px;
        background: white;
        border-radius: 12px;
        border: 1.5px solid #21A7D1;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        z-index: 10;
        max-height: 320px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .template-slide-enter-active {
        transition: opacity 200ms ease-out, transform 200ms ease-out;
    }

    .template-slide-leave-active {
        transition: opacity 150ms ease-in, transform 150ms ease-in;
    }

    .template-slide-enter-from {
        opacity: 0;
        transform: translateY(8px);
    }

    .template-slide-leave-to {
        opacity: 0;
        transform: translateY(8px);
    }

    .template_search_wrapper {
        padding: 10px 12px 8px 12px;
        border-bottom: 1px solid #e8e8e8;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        position: relative;
        z-index: 1;
    }

    .template_search_field {
        position: relative;
        display: flex;
        align-items: center;
    }

    .template_search_icon {
        position: absolute;
        left: 10px;
        width: 14px;
        height: 14px;
        opacity: 0.35;
        pointer-events: none;
        transition: opacity 150ms ease;
    }

    .template_search_field:focus-within .template_search_icon {
        opacity: 0.6;
    }

    .template_search_input {
        width: 100%;
        border: 1.5px solid #D5D5D5;
        border-radius: 8px;
        padding: 8px 12px 8px 32px;
        font-size: 14px;
        box-sizing: border-box;
        outline: none;
        transition: border-color 150ms ease;
    }

    .template_list {
        overflow-y: auto;
        flex: 1;
    }

    .template_item {
        padding: 10px 12px;
        cursor: pointer;
        border-bottom: 1px solid #f0f0f0;
        transition: background-color 150ms ease;
    }

    .template_item:last-child {
        border-bottom: none;
    }

    .template_item:hover,
    .template_item_highlighted {
        background-color: #f0f9fc;
    }

    .template_text {
        font-size: 14px;
        color: #2d2d2d;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: normal;
        line-height: 1.35;
    }

    .template_empty {
        padding: 20px 12px;
        text-align: center;
        color: #65656F;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
    }

    .template_empty_icon {
        width: 32px;
        height: 32px;
        opacity: 0.25;
    }
</style>
